import React, { useState, useEffect, useRef } from 'react'
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useHistory } from 'react-router';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

//components
import Disclaimer from './components/disclaimer';
import Terms from './components/terms';
import usePlaidLink from './hooks/usePlaidLink';
import ErrorModal from '../../../body/forms/components/ErrorModal'

//assets
import bankShieldIcon from '../../../../assets/plaid/bank-shield-icon.png';
import bulletIcons from '../../../../assets/plaid/bullet-icons.png';

//css
import '../../../../assets/prequal/linkAccount.css';
import '../../../../assets/progressBar.css';

//classes
import AxiosWrapper from '../../../../services/axios';
import Processing from './components/processing';
import plaidService from "../../../../services/plaidService";

// "View Terms & Conditions" / "About Verification" buttons are kept below but
// not rendered - the current design surfaces Terms only via the inline
// checkbox link. Flip this back on if the standalone buttons are needed again.
const SHOW_LEGACY_ACTIONS = false;

function Plaid() {

    const clientCommon = new AxiosWrapper();

    const plaidLinkMode =
    process.env.REACT_APP_PLAID_LINK_MODE || "embedded";

    const isEmbeddedPlaid = plaidLinkMode.toLowerCase() === "embedded";
    const plaidEmbeddedContainerId = "plaid-embedded-link-container";

    const history = useHistory();

    //easy-peasy state declarations
    const brandSettings = useStoreState(state => state.siteSettingsModel.brandSettings);
    const setApplicationResult = useStoreActions(actions => actions.applicationResultModel.setApplicationResult);
    const setdisplayTerms = useStoreActions((actions) => actions.setDisplayPrequalTerms);
    const redirectPath = { 'PREQUAL' : 'terms', 'default' : 'approved', 'ACTIVATION_PAYMENT': 'pendfee', 'PREQUALFIT': 'termsOffer' };
    const setting = useStoreState(state => state.siteSettingsModel);
    const setdisplayResult = useStoreActions((actions) => actions.setDisplayResult);

    //useState
    const [linkToken, setLinkToken] = useState(null);
    const [showProcessing, setShowProcessing] = useState(false);
    const webAssetsBaseUrlSettings = useStoreState(state => state.siteSettingsModel.webAssetsBaseUrlSettings);
    const applicationResult = useStoreState(state => state.applicationResultModel.applicationResult);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [showPlaidTerms, setShowPlaidTerms] = useState(false);
    const [hasAgreed, setHasAgreed] = useState(false);
    const domain = useStoreState(state => state.siteSettingsModel.domain);
    const getRoutingSettings = useStoreActions(state => state.routingSettingsModel.getRoutingSettings);
    const linking = new plaidService();
    const [plaidUserId, setPlaidUserId] = useState(null);
    // Only the required checkbox gates the button - viewing the Terms modal is optional.
    const isDisabled = !hasAgreed;
    const [errorShow, setErrorShow] = useState(false);
    const closeError = () => setErrorShow(false);
    const [errorMessage, setErrorMessage] = useState(false);

    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => { isMountedRef.current = false; };
    }, []);

    useEffect(() => {
        getRoutingSettings({ Url: domain, Route: window.location.pathname});
    },[])

    const triggerAccountLinking = async () => {
        linking.confirmAccountLinking(applicationResult.ref_number, plaidUserId);
    }

    const waitForApplicationResult = async () => {
        try {
            let result = await clientCommon.keepGetApplicationResult(
                "get-application-result",
                { applicantId: applicationResult.ref_number }
            );
    
            if (!isMountedRef.current) return;
    
            result = result || { ...applicationResult, result: "sys_error" };
    
            if (["APPROVED", "OFFER"].includes(result.result?.toUpperCase())) {
                setApplicationResult(result);
                setdisplayTerms(true);
    
                const objective = redirectPath[setting.mainSettings.Flow] || redirectPath.default;
    
                history.push(setting.MainPath + objective + window.location.search);
                return;
            }
    
            if (!result.decline_message?.trim() && result.reason?.toUpperCase() === "SYS_ERROR") {
                result = { ...result, result: "SYS_ERROR" };
            }
    
            setApplicationResult(result);
            setdisplayResult(true);
            history.push(setting.MainPath + "decision" + window.location.search);
        } catch (err) {
            if (!isMountedRef.current) return;
    
            setShowProcessing(false);
            setErrorMessage(err.message);
            setErrorShow(true);
        }
    };

    const handlePlaidSuccess = () => {
        setLinkToken(null);
        setShowProcessing(true);
        triggerAccountLinking();
        
        setTimeout(() => {
            if (isMountedRef.current) {
                waitForApplicationResult();
            }
        }, 10000);
    }

    const handleAddInstantly = async () => {
        try {
            const tokenResponse = await linking.createLinkToken(applicationResult.ref_number);
    
            if (!isMountedRef.current) return;
    
            if (!tokenResponse || tokenResponse.Exception || !tokenResponse.LinkToken) {
                const result = { ...applicationResult, result: "sys_error" };
                setApplicationResult(result);
                setdisplayResult(true);
                history.push(setting.MainPath + "decision" + window.location.search);
                return;
            }
    
            setPlaidUserId(tokenResponse.UserId);
            setLinkToken(tokenResponse.LinkToken);

        } catch {
            if (!isMountedRef.current) return;
    
            const result = { ...applicationResult, result: "sys_error" };
            setApplicationResult(result);
            setdisplayResult(true);
            history.push(setting.MainPath + "decision" + window.location.search);
        }   
    }

    const handleExit = async () => {
        const applicantId = applicationResult?.ref_number;

        try {
            await linking.processDecline(applicantId);
            history.replace(`${setting.MainPath}${window.location.search}`);
        } catch (error) {
            if (!isMountedRef.current) return;

            setErrorMessage(error.message || "Unable to exit the application.");
            setErrorShow(true);
        }
    };

    const handleViewTerms = (e) => {
        if (e) e.preventDefault();
    
        setShowPlaidTerms(true);
    };

    const handleRetry = () => {
        setErrorShow(false);
    };

    const handlePlaidExit = (err, metadata) => {
        setLinkToken(null);
    }

    usePlaidLink({
        linkToken,
        mode: isEmbeddedPlaid ? "embedded" : "modal",
        embeddedContainerId: plaidEmbeddedContainerId,
        onSuccess: handlePlaidSuccess,
        onExit: handlePlaidExit,
        onError: (err) => {
            setErrorMessage(err.message);
            setErrorShow(true);
        },
    });    


    return (
    <>
        <ErrorModal size='sm' show={errorShow} message={errorMessage} closeError={closeError} onRetry={handleRetry} />
        <div className="ribbon" style={{ backgroundColor: '#000', textAlign: 'center', display: 'flex', justifyContent: 'center', width: '100%' }}>
            <h1 style={{ textAlign: 'center', width: '100%' }}>{showProcessing ? 'Verifying your information...' : 'Connect your bank to check your eligibility'}</h1>
        </div>
        <div className='container'>
            <div className='row'>
                <div className='col-12 text-center'>
                    <img src={bankShieldIcon} alt="" className="plaid-hero-icon" />
                </div>
            </div>
            {
                showProcessing ?
                <Processing /> :
                <>
                    <div className='row'>
                        <div className='col-12 col-lg-8 offset-lg-2 text-center'>
                            <h4 style={{ fontWeight: 600 }}>
                                We use Plaid to quickly and securely add your bank account to see if you qualify for a credit card
                            </h4>
                        </div>
                    </div>
                    <div className="plaid-bullets">
                        <div className="plaid-bullets-inner">
                            <span className="plaid-bullet-row"><span className="plaid-bullet-icon plaid-bullet-icon-top" style={{ backgroundImage: `url(${bulletIcons})` }} /> Fast and easy</span>
                            <span className="plaid-bullet-row"><span className="plaid-bullet-icon plaid-bullet-icon-bottom" style={{ backgroundImage: `url(${bulletIcons})` }} /> Secure and encrypted</span>
                        </div>
                    </div>
                    {SHOW_LEGACY_ACTIONS && (
                    <div className="row link-account-section">
                        <div className='col-xs-12 col-sm-12 col-md-6  col-lg-6' style={{marginBottom: "30px"}}>
                            <img src={(brandSettings.ProductName === "Surge") ? webAssetsBaseUrlSettings.CdnImage+"Surge/card-silver.png" : brandSettings.CardImage} alt={brandSettings.ProductName}/>
                        </div>
                        <div className='col-xs-12 col-sm-12 col-md-6 col-lg-6'>
                            <div className='container' style={{marginTop: "10px"}}>
                                <div className='row'>
                                    <div className='col-12'>
                                        <Button style={{ backgroundColor: brandSettings.Primary, width: "100%" }} onClick={() => handleViewTerms()}>View Terms & Conditions</Button>
                                    </div>
                                </div>
                                <div className='row'>
                                    <div className='col-12'>
                                        <p>You are required to view the Terms and Conditions of this offer.</p>
                                    </div>
                                </div>
                                <div className='row'>
                                    <div className='col-12'>
                                        <Button style={{ backgroundColor: brandSettings.Primary, width: "100%" }} onClick={() => setShowDisclaimer(true)}>About Verification</Button>
                                    </div>
                                </div>
                                <div className='row'>
                                    <div className='col-12'>
                                        <p>See more about verifying your bank account and how it pertains to this credit line offer.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}
                    <div className="row link-account-section">
                        <div className='col-10 offset-1'>
                            <div className="form-check check-fields">
                                <div className="input-container">
                                    <input className="form-check-input" type="checkbox" onChange={(e) => setHasAgreed(e.target.checked)} />
                                    <label className="form-check-label">
                                        <h5 style={{ fontWeight: 'normal', color: '#6c757d' }}>
                                            By clicking this box, you agree that you have read the full <span id="plaid-terms-link" onClick={() => handleViewTerms()}>Terms and conditions</span> of the {brandSettings.ProductName} Mastercard® credit card offer
                                        </h5>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row link-account-section mb-4">
                        <div className="col-12 text-center mb-3">
                            {isEmbeddedPlaid && linkToken ? (
                                <div
                                    id={plaidEmbeddedContainerId}
                                    style={{
                                        width: "100%",
                                        maxWidth: "350px",
                                        minWidth: "350px",
                                        height: "420px",
                                        minHeight: "300px",
                                        margin: "0 auto",
                                    }}
                                />
                            ) : (
                                <Button
                                    style={{
                                        backgroundColor: brandSettings.Secondary,
                                        minWidth: "18.75rem",
                                    }}
                                    onClick={() => handleAddInstantly()}
                                    disabled={isDisabled || !!linkToken}
                                >
                                    <FontAwesomeIcon icon={faLock} /> &nbsp;Add instantly
                                </Button>
                            )}
                        </div>
                        <div className="col-12 text-center">
                            <span id="exit-application-link" onClick={handleExit}>Exit application</span>
                        </div>
                    </div>
                </>
            }
        </div>

        <div id="plaid-modal-wrapper">
            <Disclaimer props={{setShowDisclaimer: setShowDisclaimer, showDisclaimer: showDisclaimer}}/>
            <Terms props={{setShowPlaidTerms: setShowPlaidTerms, showPlaidTerms: showPlaidTerms}}/>
        </div>
    </>
  )
}

export default Plaid